import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Pagination from "components/pagination";
import { Toaster, toast } from "react-hot-toast";
import { FaEdit, FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import { packagePath, packageRequest } from "./packageApi";

const PAGE_SIZE = 10;

export default function ServicePackages() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const loadPackages = useCallback(async (page = 1) => {
    setLoading(true);
    const params = { page: String(page), limit: String(PAGE_SIZE) };
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    try {
      const result = await packageRequest("admin/packages", "GET", null, params);
      setPackages(Array.isArray(result.items) ? result.items : []);
      setPagination({
        page: Number(result.pagination?.page || page),
        total: Number(result.pagination?.total || 0),
        total_pages: Math.max(1, Number(result.pagination?.total_pages || 1)),
      });
    } catch (error) {
      toast.error(error.message || "Unable to load packages.");
      setPackages([]);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadPackages(1); }, [loadPackages]);

  const deletePackage = async (pkg) => {
    if (!window.confirm(`Delete “${pkg.name}”?`)) return;
    try {
      await packageRequest(packagePath(pkg.id), "DELETE");
      toast.success("Package deleted.");
      loadPackages(packages.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch (error) { toast.error(error.message || "Unable to delete package."); }
  };

  return <DashboardLayout>
    <DashboardNavbar />
    <Toaster position="top-center" />
    <div className="py-3">
      <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div><h2 className="text-lg font-bold text-slate-900">Service packages</h2><p className="mt-1 text-xs text-slate-500">{pagination.total} total packages</p></div>
          <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-[minmax(220px,1fr)_170px_auto]">
            <form onSubmit={(event) => { event.preventDefault(); setFilters((current) => ({ ...current, search: searchInput.trim() })); }} className="relative">
              <FaSearch className="absolute left-3 top-3 text-slate-400" />
              <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search packages" className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-500" />
            </form>
            <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm">
              <option value="">All statuses</option><option value="draft">Draft</option><option value="active">Active</option><option value="inactive">Inactive</option>
            </select>
            <button onClick={() => navigate("/service-packages/create")} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-indigo-700"><FaPlus /> Create package</button>
          </div>
        </div>

        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Package</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Services</th><th className="px-4 py-3">Version</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500">Loading packages...</td></tr> : packages.length === 0 ? <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500">No service packages found.</td></tr> : packages.map((pkg) => <tr key={pkg.id} className="hover:bg-slate-50">
                <td className="px-4 py-3"><p className="font-bold text-slate-900">{pkg.name}</p><p className="mt-1 max-w-md truncate text-xs text-slate-500">{pkg.description || "No description"}</p><p className="mt-1 text-xs text-slate-400">ID #{pkg.id}</p></td>
                <td className="px-4 py-3 text-slate-600">{pkg.slug || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{pkg.item_count ?? pkg.items?.filter((item) => !item.parent_item_id).length ?? 0}</td>
                <td className="px-4 py-3 text-slate-600">{pkg.version ?? "—"}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${pkg.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{pkg.status || "draft"}</span></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-2"><button title="Manage package" onClick={() => navigate(`/service-packages/${pkg.id}/edit`)} className="rounded-lg bg-indigo-50 p-2.5 text-indigo-700 hover:bg-indigo-100"><FaEdit /></button><button title="Delete package" onClick={() => deletePackage(pkg)} className="rounded-lg bg-red-50 p-2.5 text-red-700 hover:bg-red-100"><FaTrash /></button></div></td>
              </tr>)}
            </tbody>
          </table>
        </div>
        {!loading && pagination.total_pages > 1 && <div className="mt-5"><Pagination currentPage={pagination.page} totalPages={pagination.total_pages} onPageChange={loadPackages} recordsPerPage={PAGE_SIZE} onRecordsPerPageChange={() => {}} totalRecords={pagination.total} count={packages.length} compact /></div>}
      </section>
    </div>
  </DashboardLayout>;
}
